import { Search, Clock, X } from 'lucide-react';
import { memo, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createSearchParams, useLocation, useNavigate } from 'react-router-dom';
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { fetchProductsBySearch } from '../../../services/product-api';
import { useDebouncedValue } from '../../../hooks/useDebouncedValue';
import {
  getProductPath,
  useCategoryLookup,
} from '../../../hooks/useCategoryLookup';
import {
  addRecentSearch,
  clearRecentSearches,
  getRecentSearches,
} from '../../../utils/recentSearches';
import {
  normaliseSearchQuery,
  searchProductsQuery,
} from '../../../pages/searchProductResult/searchProductsQuery';
import { optimizeImageUrl } from '../../../utils/optimizeImageUrl';
import { formatCurrency } from '../../../utils/FormatCurrency';

const MIN_CHARS = 2;
const MAX_SUGGESTIONS = 6;

const SearchQuery = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const listId = useId();
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [recent, setRecent] = useState(getRecentSearches);

  const typed = normaliseSearchQuery(term);
  const debounced = normaliseSearchQuery(useDebouncedValue(term, 250));
  const showSuggestions = typed.length >= MIN_CHARS;

  // Product suggestions while typing. Requests wait for a pause in typing,
  // and outdated ones are cancelled.
  const { data, isFetching } = useQuery({
    queryKey: ['search-suggest', debounced],
    queryFn: ({ signal }) =>
      fetchProductsBySearch(debounced, 1, { limit: MAX_SUGGESTIONS, signal }),
    enabled: open && debounced.length >= MIN_CHARS,
    staleTime: 1000 * 60 * 5,
    placeholderData: keepPreviousData,
  });
  const { bySubCategoryId } = useCategoryLookup({ enabled: open });

  const items = useMemo(() => {
    if (!showSuggestions) {
      return recent.map((label) => ({ type: 'recent', label }));
    }
    const products = (data?.data?.results?.products ?? []).slice(
      0,
      MAX_SUGGESTIONS
    );
    return [
      { type: 'query', label: typed },
      ...products.map((product) => ({ type: 'product', product })),
    ];
  }, [showSuggestions, recent, data, typed]);

  // Reset when the page changes
  useEffect(() => {
    setTerm('');
    setOpen(false);
  }, [location.key]);

  // Close when clicking anywhere else
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e) => {
      if (!containerRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  const close = () => {
    setOpen(false);
    setActiveIndex(-1);
    inputRef.current?.blur();
  };

  const goToResults = (value) => {
    const q = normaliseSearchQuery(value);
    if (!q) return;
    addRecentSearch(q);
    setRecent(getRecentSearches());
    // Start the results request and the results page code now, in parallel
    // with navigation, instead of after the page has loaded
    queryClient.prefetchQuery(searchProductsQuery(q, 1));
    import('../../../pages/searchProductResult/SearchProductResult');
    close();
    navigate({
      pathname: '/search-results',
      search: `?${createSearchParams({ q })}`,
    });
  };

  const selectItem = (item) => {
    if (item.type !== 'product') {
      goToResults(item.label);
      return;
    }
    const path = getProductPath(item.product, bySubCategoryId);
    if (!path) {
      goToResults(item.product.productName);
      return;
    }
    if (typed) {
      addRecentSearch(typed);
      setRecent(getRecentSearches());
    }
    close();
    navigate(path);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActiveIndex((i) => Math.min(i + 1, items.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, -1));
    } else if (e.key === 'Enter' && open && activeIndex >= 0) {
      e.preventDefault();
      selectItem(items[activeIndex]);
    } else if (e.key === 'Escape') {
      close();
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    goToResults(term);
  };

  const showDropdown =
    open && (items.length > 0 || (showSuggestions && isFetching));
  const optionId = (index) => `${listId}-option-${index}`;

  return (
    <div className="flex w-full lg:w-2/5 xl:w-[38%]" ref={containerRef}>
      <form
        onSubmit={onSubmit}
        className="w-full font-duplet"
        role="search"
        autoComplete="off"
      >
        <label htmlFor={`${listId}-input`} className="sr-only">
          Search for products
        </label>
        <div className="relative flex items-center">
          <div className="w-full rounded-full p-[2px] bg-gradient-to-r from-[#16B4EF] via-[#7E2A83] to-[#DB1C5E] animate-gradient-x bg-[length:200%_200%]">
            <div className="relative flex items-center bg-white rounded-full">
              <input
                ref={inputRef}
                type="text"
                id={`${listId}-input`}
                role="combobox"
                aria-label="Search for products"
                aria-expanded={showDropdown}
                aria-controls={listId}
                aria-autocomplete="list"
                aria-activedescendant={
                  showDropdown && activeIndex >= 0
                    ? optionId(activeIndex)
                    : undefined
                }
                enterKeyHint="search"
                value={term}
                onChange={(e) => {
                  setTerm(e.target.value);
                  setActiveIndex(-1);
                  setOpen(true);
                }}
                onFocus={() => setOpen(true)}
                onKeyDown={onKeyDown}
                placeholder="What do you need today?"
                className="rounded-full pl-6 pr-16 py-2 text-base w-full
                           focus:outline-none
                           transition-all motion-safe:duration-200
                           bg-white font-duplet placeholder-[#9CA3AF]"
              />
              <button
                type="submit"
                aria-label="Submit search"
                className="absolute right-4 flex items-center justify-center
                           transition-all motion-safe:duration-200 hover:scale-110 active:scale-95
                           before:content-[''] before:absolute before:left-[-12px] before:h-6 before:w-[1px] before:bg-gray-300"
              >
                <Search
                  size={24}
                  className="text-[#7E2A83]"
                  aria-hidden="true"
                />
              </button>
            </div>
          </div>

          {showDropdown && (
            <ul
              id={listId}
              role="listbox"
              aria-label="Search suggestions"
              className="absolute left-0 right-0 top-full mt-2 z-50 bg-white rounded-2xl shadow-lg border border-gray-100 py-2 max-h-[70vh] overflow-y-auto"
            >
              {!showSuggestions && recent.length > 0 && (
                <li
                  role="presentation"
                  className="flex items-center justify-between px-4 pt-1 pb-2 text-xs text-[#6B6B6B]"
                >
                  <span>Recent searches</span>
                  <button
                    type="button"
                    className="hover:underline"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      clearRecentSearches();
                      setRecent([]);
                    }}
                  >
                    Clear
                  </button>
                </li>
              )}

              {items.map((item, index) => (
                <li
                  key={
                    item.type === 'product'
                      ? item.product.productID
                      : `${item.type}-${item.label}`
                  }
                  id={optionId(index)}
                  role="option"
                  aria-selected={index === activeIndex}
                  // Keep focus in the input so the list doesn't close first
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => selectItem(item)}
                  onMouseEnter={() => setActiveIndex(index)}
                  className={`flex items-center gap-3 px-4 py-2 cursor-pointer text-sm text-[#222224] ${
                    index === activeIndex ? 'bg-gray-100' : ''
                  }`}
                >
                  {item.type === 'product' ? (
                    <ProductSuggestion product={item.product} />
                  ) : (
                    <>
                      {item.type === 'recent' ? (
                        <Clock size={16} className="text-gray-400 shrink-0" />
                      ) : (
                        <Search size={16} className="text-gray-400 shrink-0" />
                      )}
                      <span className="truncate">
                        {item.type === 'query' ? (
                          <>
                            Search for{' '}
                            <strong>&ldquo;{item.label}&rdquo;</strong>
                          </>
                        ) : (
                          item.label
                        )}
                      </span>
                    </>
                  )}
                </li>
              ))}

              {showSuggestions && isFetching && items.length <= 1 && (
                <li
                  role="presentation"
                  className="px-4 py-2 text-sm text-[#6B6B6B]"
                >
                  Searching…
                </li>
              )}
            </ul>
          )}

          {term && (
            <button
              type="button"
              aria-label="Clear search"
              className="absolute right-14 p-1 text-gray-400 hover:text-gray-600"
              onClick={() => {
                setTerm('');
                setActiveIndex(-1);
                inputRef.current?.focus();
              }}
            >
              <X size={16} />
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

const ProductSuggestion = ({ product }) => {
  const salePrice = Number(product.fairAppSalesPrice);
  const price = salePrice > 0 ? salePrice : product.fairAppPrice;

  return (
    <>
      <span className="w-10 h-10 shrink-0 rounded-lg bg-gray-100 overflow-hidden flex items-center justify-center">
        {product.coverImage && (
          <img
            src={optimizeImageUrl({ url: product.coverImage, width: 80 })}
            alt=""
            width={40}
            height={40}
            decoding="async"
            className="w-full h-full object-cover"
          />
        )}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate">{product.productName}</span>
        {Number(price) > 0 && (
          <span className="block text-xs text-[#6B6B6B]">
            {formatCurrency(price)}
          </span>
        )}
      </span>
    </>
  );
};

export default memo(SearchQuery);
